import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Send, Loader2, MessageSquare, Plus, Trash2, Bot, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIChat() {
    const { token } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [currentSession, setCurrentSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const messagesEndRef = useRef(null);

    const fetchSessions = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/chat/sessions`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSessions(data);
            }
        } catch (error) {
            console.error('Failed to fetch sessions:', error);
        }
    }, [token]);

    const fetchMessages = useCallback(async (sessionId) => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/chat/history/${sessionId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setMessages(data);
            }
        } catch (error) {
            console.error('Failed to fetch messages:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);

    useEffect(() => {
        if (currentSession) {
            fetchMessages(currentSession);
        }
    }, [currentSession, fetchMessages]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const startNewChat = () => {
        setCurrentSession(null);
        setMessages([]);
    };

    const sendMessage = async () => {
        if (!input.trim() || sending) return;

        const userMessage = input.trim();
        setInput('');
        setSending(true);

        // Optimistically add user message
        const tempUserMsg = { role: 'user', content: userMessage, created_at: new Date().toISOString() };
        setMessages(prev => [...prev, tempUserMsg]);

        try {
            const res = await fetch(`${API_URL}/api/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    message: userMessage,
                    session_id: currentSession
                })
            });

            if (res.ok) {
                const data = await res.json();
                if (!currentSession) {
                    setCurrentSession(data.session_id);
                }
                setMessages(prev => [...prev, { role: 'assistant', content: data.response, created_at: new Date().toISOString() }]);
                fetchSessions();
            } else {
                throw new Error('Failed to send');
            }
        } catch (error) {
            toast.error('Failed to get response');
            setMessages(prev => prev.slice(0, -1));
        } finally {
            setSending(false);
        }
    };

    const deleteSession = async (sessionId) => {
        if (!window.confirm('Delete this conversation?')) return;
        try {
            await fetch(`${API_URL}/api/chat/sessions/${sessionId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (currentSession === sessionId) {
                setCurrentSession(null);
                setMessages([]);
            }
            fetchSessions();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    return (
        <div className="h-[calc(100vh-8rem)] flex gap-4">
            {/* Sidebar */}
            <div className="w-64 flex-shrink-0 hidden md:block">
                <Card className="h-full flex flex-col">
                    <CardHeader className="pb-3">
                        <Button onClick={startNewChat} className="w-full" data-testid="new-chat-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            New Chat
                        </Button>
                    </CardHeader>
                    <CardContent className="flex-1 overflow-y-auto">
                        <div className="space-y-2">
                            {sessions.map((session) => (
                                <div
                                    key={session.session_id}
                                    className={`p-3 rounded-lg cursor-pointer group flex items-start justify-between ${
                                        currentSession === session.session_id 
                                            ? 'bg-primary/10' 
                                            : 'hover:bg-muted'
                                    }`}
                                    onClick={() => setCurrentSession(session.session_id)}
                                >
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm truncate">{session.last_message}</p>
                                        <p className="text-xs text-muted-foreground">{session.message_count} messages</p>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 opacity-0 group-hover:opacity-100"
                                        onClick={(e) => { e.stopPropagation(); deleteSession(session.session_id); }}
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Chat Area */}
            <Card className="flex-1 flex flex-col">
                <CardHeader className="border-b">
                    <CardTitle className="flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-violet-500" />
                        AI Support Chat
                    </CardTitle>
                </CardHeader>
                
                <CardContent className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <div className="flex items-center justify-center h-full">
                            <Loader2 className="w-8 h-8 animate-spin" />
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center">
                            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center mb-4">
                                <Bot className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="font-semibold text-lg mb-2">Hi! I'm here to help</h3>
                            <p className="text-muted-foreground max-w-md">
                                Ask me anything about autoimmune conditions, coping strategies, or just share how you're feeling. 
                                I'm here to support you on this journey.
                            </p>
                            <div className="flex flex-wrap gap-2 mt-6 justify-center">
                                {['What is AIP diet?', 'Tips for managing fatigue', 'How to explain to family'].map((q) => (
                                    <Button key={q} variant="outline" size="sm" onClick={() => setInput(q)}>
                                        {q}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {messages.map((msg, idx) => (
                                <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                                    {msg.role === 'assistant' && (
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                                            <Bot className="w-4 h-4 text-white" />
                                        </div>
                                    )}
                                    <div className={`max-w-[80%] p-4 rounded-2xl ${
                                        msg.role === 'user' 
                                            ? 'bg-primary text-primary-foreground rounded-br-md' 
                                            : 'bg-muted rounded-bl-md'
                                    }`}>
                                        <p className="whitespace-pre-wrap">{msg.content}</p>
                                    </div>
                                    {msg.role === 'user' && (
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center flex-shrink-0">
                                            <User className="w-4 h-4 text-white" />
                                        </div>
                                    )}
                                </div>
                            ))}
                            {sending && (
                                <div className="flex gap-3">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                                        <Bot className="w-4 h-4 text-white" />
                                    </div>
                                    <div className="bg-muted p-4 rounded-2xl rounded-bl-md">
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                    )}
                </CardContent>

                <div className="p-4 border-t">
                    <div className="flex gap-2">
                        <Input
                            placeholder="Type your message..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                            disabled={sending}
                            data-testid="chat-input"
                        />
                        <Button onClick={sendMessage} disabled={sending || !input.trim()} data-testid="send-btn">
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                        I provide support and information, not medical advice. Always consult your healthcare team.
                    </p>
                </div>
            </Card>
        </div>
    );
}
